import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useKeyboard, KEYBOARD_LAYOUTS } from '../context/KeyboardContext';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Sun, Moon, KeyRound, Printer, Copy, RefreshCw, Check, Send, Lock, Wifi, Usb, Bluetooth, Save, Search, Monitor, ShieldCheck, AlertTriangle, Cloud, ChefHat, Wine, Smartphone, ChevronLeft, Keyboard, HelpCircle, ChevronDown, FileText } from 'lucide-react';
import { FiscalSettings } from '../components/settings/FiscalSettings';
import { InvoicesList } from '../components/settings/InvoicesList';
import { SumUpSettings } from '../components/settings/SumUpSettings';
import { SecuritySettings } from '../components/settings/SecuritySettings';
import { eposTestPrint, printerBaseUrl } from '../lib/eposPrint';
import { clearPrinterConfigCache } from '../lib/receipt';
import { clearStationsCache } from '../lib/comande';
import { connectBtPrinter, btTestPrint, isWebBluetoothSupported, btConnectedName } from '../lib/btPrint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { enabled: kbdEnabled, layout: kbdLayout, setEnabled: setKbdEnabled, setLayout: setKbdLayout } = useKeyboard();
  const [s, setS] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [pc, setPc] = useState(null);
  const [printer, setPrinter] = useState(null);
  const [savingPrinter, setSavingPrinter] = useState(false);
  const [testingEpos, setTestingEpos] = useState(false);
  const [btName, setBtName] = useState(null);
  const [btBusy, setBtBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stationPrinters, setStationPrinters] = useState({});
  const [savingStations, setSavingStations] = useState(false);
  const [testingStation, setTestingStation] = useState('');
  // Stampante CAMERIERE (server-side, per attività): stampante di rete Wi-Fi.
  const [waiterPrn, setWaiterPrn] = useState({ enabled: false, mode: 'wifi', protocol: 'https', host: '', port: 0, paper_width: 80 });
  const [testingWaiter, setTestingWaiter] = useState(false);
  const [savingWaiter, setSavingWaiter] = useState(false);
  // Categoria di impostazioni attualmente aperta (null = mostra l'hub categorie).
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get('/settings').then(({ data }) => setS(data));
    api.get('/licenses').then(({ data }) => setLicenses(data)).catch(() => {});
    api.get('/print/settings').then(({ data }) => {
      setPc(data); setPrinter(data.printer || null);
      setStationPrinters(data.station_printers || {});
      if (data.waiter_printer) setWaiterPrn((w) => ({ ...w, ...data.waiter_printer }));
    }).catch(() => {});
  }, []);

  const setPf = (field, value) => setPrinter((p) => ({ ...(p || {}), [field]: value }));

  const setSp = (station, field, value) =>
    setStationPrinters((sp) => ({ ...sp, [station]: { ...(sp[station] || {}), [field]: value } }));

  // --- Stampante cameriere (salvata sul server, per attività) ---
  const setWf = (field, value) => setWaiterPrn((w) => ({ ...(w || {}), [field]: value }));
  const saveWaiter = async () => {
    setSavingWaiter(true);
    try {
      const { data } = await api.put('/print/settings', { waiter_printer: waiterPrn });
      if (data?.waiter_printer) setWaiterPrn((w) => ({ ...w, ...data.waiter_printer }));
      toast.success(t('settings.waiterPrinterSaved', { defaultValue: 'Stampante cameriere salvata' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSavingWaiter(false); }
  };
  const testWaiter = async () => {
    setTestingWaiter(true);
    try {
      if (!waiterPrn.host) { toast.error(t('settings.printerHostRequired')); setTestingWaiter(false); return; }
      await eposTestPrint({ host: waiterPrn.host, protocol: waiterPrn.protocol || 'https', port: waiterPrn.port || 0, paper_width: waiterPrn.paper_width || 80 }, s?.restaurant_name || 'Vexel POS', null);
      toast.success(t('settings.testPrintSent', { defaultValue: 'Stampa di prova inviata' }));
    } catch (e) {
      toast.error(e?.message || 'Stampa di prova fallita');
    } finally { setTestingWaiter(false); }
  };

  // IP delle stampanti memorizzati: usati come suggerimenti (datalist) nei campi IP.
  const savedIps = pc?.saved_printer_ips || [];

  // Pannello "Guida" richiudibile (chiuso di default) per snellire la pagina.
  const Guide = ({ title, testid, children }) => (
    <details className="group mt-4 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground" data-testid={testid}>
      <summary className="flex cursor-pointer select-none items-center gap-2 font-semibold text-foreground marker:content-none [&::-webkit-details-marker]:hidden">
        <HelpCircle className="h-3.5 w-3.5 text-primary" />
        {title}
        <ChevronDown className="ml-auto h-3.5 w-3.5 transition-transform group-open:rotate-180" />
      </summary>
      <div className="mt-2 space-y-1.5">{children}</div>
    </details>
  );


  const saveStations = async () => {
    setSavingStations(true);
    try {
      const { data } = await api.put('/print/settings', { station_printers: stationPrinters });
      setPc((p) => ({ ...p, ...data }));
      setStationPrinters(data.station_printers || {});
      clearStationsCache();
      toast.success(t('settings.printerSaved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('settings.updateError')); }
    finally { setSavingStations(false); }
  };

  const testStationPrint = async (station) => {
    const cfg = stationPrinters[station];
    if (!cfg?.host) { toast.error(t('settings.enterPrinterIpFirst')); return; }
    setTestingStation(station);
    try {
      await eposTestPrint(
        { host: cfg.host, protocol: cfg.protocol || 'https', port: cfg.port || 0, paper_width: cfg.paper_width || 80 },
        s?.restaurant_name || 'Stampa',
      );
      toast.success(t('settings.testPrintSent'));
    } catch (e) { toast.error(e?.message || t('settings.error')); }
    finally { setTestingStation(''); }
  };

  const savePrinter = async () => {
    setSavingPrinter(true);
    try {
      const { data } = await api.put('/print/settings', { printer, sdp_enabled: (printer?.mode === 'sdp') });
      setPc((p) => ({ ...p, ...data }));
      setPrinter(data.printer);
      clearPrinterConfigCache();
      toast.success(t('settings.printerSaved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('settings.updateError')); }
    finally { setSavingPrinter(false); }
  };

  const testEposPrint = async () => {
    if (!printer?.host) { toast.error(t('settings.printerHostRequired')); return; }
    setTestingEpos(true);
    try {
      // Ricarica il logo aggiornato dal server (evita versioni in cache nello stato).
      let logo = pc?.receipt_logo;
      try {
        const { data } = await api.get('/print/settings');
        logo = data?.receipt_logo;
        setPc((p) => ({ ...(p || {}), receipt_logo: logo }));
      } catch { /* usa il logo già in memoria */ }
      const res = await eposTestPrint(printer, s?.restaurant_name || 'Restaurant Manager Pro', logo);
      // Mostra lo stato reale restituito dalla stampante (utile per diagnosi).
      const parts = [];
      if (res && res.battery != null) parts.push(`batteria ${res.battery}`);
      if (res && res.status != null) parts.push(`stato 0x${(res.status >>> 0).toString(16)}`);
      toast.success(t('settings.testPrintSent') + (parts.length ? ` — ${parts.join(', ')}` : ''));
    } catch (e) { toast.error(e?.message || t('settings.error')); }
    finally { setTestingEpos(false); }
  };

  const connectBt = async () => {
    setBtBusy(true);
    try {
      const name = await connectBtPrinter();
      setBtName(name);
      toast.success(t('settings.btConnected', { name }));
    } catch (e) {
      if (e?.name !== 'NotFoundError') toast.error(e?.message || t('settings.error'));
    } finally { setBtBusy(false); }
  };

  const testBtPrint = async () => {
    setBtBusy(true);
    try {
      await btTestPrint(s?.restaurant_name || 'Restaurant Manager Pro', printer?.paper_width || 80);
      setBtName(btConnectedName());
      toast.success(t('settings.testPrintSent'));
    } catch (e) { toast.error(e?.message || t('settings.error')); }
    finally { setBtBusy(false); }
  };

  const refreshDiscovered = async () => {
    try {
      const { data } = await api.get('/print/settings');
      setPc(data); setPrinter(data.printer || null);
      toast.success(t('settings.printersRefreshed'));
    } catch (e) { toast.error(t('settings.error')); }
  };

  const togglePrintAgent = async (val) => {
    try {
      const { data } = await api.put('/print/settings', { agent_enabled: val });
      setPc((p) => ({ ...p, ...data }));
      toast.success(val ? t('settings.autoPrintOn') : t('settings.autoPrintOff'));
    } catch (e) { toast.error(t('settings.updateError')); }
  };
  const regenerateToken = async () => {
    try {
      const { data } = await api.post('/print/settings/regenerate-token');
      setPc((p) => ({ ...p, agent_token: data.agent_token }));
      toast.success(t('settings.newTokenGenerated'));
    } catch (e) { toast.error(t('settings.error')); }
  };
  const testPrint = async () => {
    try { await api.post('/print/test'); toast.success(t('settings.testPrintQueued')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('settings.enableAgentFirst')); }
  };
  const copyToken = async () => {
    try { await navigator.clipboard.writeText(pc?.agent_token || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) {}
  };

  const activeLicense = licenses.find((l) => l.activated);

  if (!s) return <div className="p-6 text-muted-foreground">{t('settings.loading')}</div>;

  // Categorie dell'hub Impostazioni: ogni voce apre solo le proprie schede.
  const CATS = [
    { key: 'business', label: t('settings.catBusiness'), Icon: Lock },
    { key: 'appearance', label: t('settings.catAppearance'), Icon: Sun },
    { key: 'keyboard', label: t('settings.catKeyboard'), Icon: Keyboard },
    { key: 'printers', label: t('settings.catPrinters'), Icon: Printer },
    { key: 'license', label: t('settings.catLicense'), Icon: KeyRound },
    { key: 'fiscal', label: t('settings.catFiscal'), Icon: ShieldCheck },
    { key: 'invoices', label: t('settings.catInvoices', { defaultValue: 'Fatture' }), Icon: FileText },
    { key: 'security', label: t('settings.catSecurity'), Icon: Lock },
  ];
  const activeCatLabel = (CATS.find((c) => c.key === activeCat) || {}).label;

  return (
    <div className="p-4 md:p-6 w-full">
      <PageHeader title={t('settings.title')} subtitle={t('settings.subtitle')} />

      {/* HUB CATEGORIE: griglia di pulsanti; cliccando si apre la categoria. */}
      {!activeCat && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="settings-categories">
          {CATS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveCat(key)}
              className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`settings-cat-${key}`}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="h-5 w-5" />
              </span>
              <span className="font-semibold">{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Barra "Indietro" quando una categoria è aperta. */}
      {activeCat && (
        <div className="mb-4 flex items-center gap-3" data-testid="settings-cat-header">
          <Button variant="ghost" size="sm" onClick={() => setActiveCat(null)} className="rounded-xl" data-testid="settings-cat-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> {t('settings.catBack')}
          </Button>
          <span className="font-semibold text-lg">{activeCatLabel}</span>
        </div>
      )}

      {activeCat === 'business' && (
      <Card className="p-5 mb-4">
        <div className="font-semibold mb-1 flex items-center gap-2"><Lock className="h-4 w-4 text-muted-foreground" /> {t('settings.businessData')}</div>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.businessDataDesc')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>{t('settings.businessName')}</Label><Input value={s.restaurant_name || ''} readOnly disabled className="mt-1.5 bg-muted/50 cursor-not-allowed" data-testid="settings-name-input" /></div>
          <div className="sm:col-span-2"><Label>{t('settings.address')}</Label><Input value={s.address || ''} readOnly disabled className="mt-1.5 bg-muted/50 cursor-not-allowed" data-testid="settings-address-input" /></div>
          <div><Label>{t('settings.phone')}</Label><Input value={s.phone || ''} readOnly disabled className="mt-1.5 bg-muted/50 cursor-not-allowed" data-testid="settings-phone-input" /></div>
          <div><Label>{t('settings.vat')}</Label><Input value={s.vat || ''} readOnly disabled className="mt-1.5 bg-muted/50 cursor-not-allowed" data-testid="settings-vat-input" /></div>
          <div><Label>{t('settings.currency')}</Label><Input value={s.currency || ''} readOnly disabled className="mt-1.5 bg-muted/50 cursor-not-allowed" data-testid="settings-currency-input" /></div>
        </div>
      </Card>
      )}

      {activeCat === 'appearance' && (
      <Card className="p-5 mb-4">
        <div className="font-semibold mb-4">{t('settings.appearance')}</div>
        <div className="flex items-center justify-between">
          <div><div className="font-medium">{theme === 'dark' ? t('settings.themeDark') : t('settings.themeLight')}</div><div className="text-sm text-muted-foreground">{t('settings.themeDesc')}</div></div>
          <Button variant="outline" onClick={toggleTheme} className="rounded-xl">{theme === 'dark' ? <><Sun className="h-4 w-4 mr-2" /> {t('settings.light')}</> : <><Moon className="h-4 w-4 mr-2" /> {t('settings.dark')}</>}</Button>
        </div>
      </Card>
      )}

      {activeCat === 'keyboard' && (
      <Card className="p-5 mb-4" data-testid="keyboard-settings-card">
        <div className="font-semibold mb-1 flex items-center gap-2"><Keyboard className="h-4 w-4 text-muted-foreground" /> {t('settings.keyboardTitle')}</div>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.keyboardDesc')}</p>

        {!isAdmin && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-border p-3 text-sm text-muted-foreground" data-testid="keyboard-admin-only">
            <Lock className="h-4 w-4 shrink-0" /> {t('settings.keyboardAdminOnly')}
          </div>
        )}

        <div className="flex items-center justify-between gap-4 py-2">
          <div>
            <div className="font-medium">{t('settings.keyboardEnable')}</div>
            <div className="text-sm text-muted-foreground">{t('settings.keyboardEnableDesc')}</div>
          </div>
          <Switch checked={kbdEnabled} onCheckedChange={setKbdEnabled} disabled={!isAdmin} data-testid="keyboard-enable-switch" />
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <Label>{t('settings.keyboardLayout')}</Label>
          <p className="text-sm text-muted-foreground mb-2">{t('settings.keyboardLayoutDesc')}</p>
          <ToggleGroup
            type="single"
            value={kbdLayout}
            onValueChange={(v) => v && isAdmin && setKbdLayout(v)}
            className="grid grid-cols-2 sm:grid-cols-4 gap-2"
            data-testid="keyboard-layout-toggle"
          >
            {KEYBOARD_LAYOUTS.map((lay) => (
              <ToggleGroupItem
                key={lay}
                value={lay}
                disabled={!isAdmin}
                className="h-auto flex-col items-start gap-0.5 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary disabled:opacity-60"
                data-testid={`keyboard-layout-${lay}`}
              >
                <span className="font-mono font-bold uppercase tracking-wide">{lay}</span>
                <span className="text-[11px] text-muted-foreground">{t(`settings.keyboardLayoutName.${lay}`)}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </Card>
      )}

      {activeCat === 'printers' && (<>
      <Card className="p-5 mb-4" data-testid="settings-printers-card">
        <div className="font-semibold mb-1 flex items-center gap-2"><Printer className="h-4 w-4" /> {t('settings.thermalPrinters')}</div>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.thermalDesc')}</p>

        {/* ===== Modalità di stampa ===== */}
        <div className="mb-4">
          <div className="font-medium mb-1">{t('settings.printMode')}</div>
          <p className="text-sm text-muted-foreground mb-3">{t('settings.printModeDesc')}</p>
          <ToggleGroup type="single" value={printer?.mode || 'device'} onValueChange={(v) => v && setPf('mode', v)} className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="print-mode-toggle">
            <ToggleGroupItem value="sdp" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="mode-sdp">
              <span className="flex items-center gap-2 font-semibold"><Cloud className="h-4 w-4" /> {t('settings.modeSdp')}</span>
              <span className="text-xs text-muted-foreground font-normal">{t('settings.modeSdpHint')}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="device" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="mode-device">
              <span className="flex items-center gap-2 font-semibold"><Wifi className="h-4 w-4" /> {t('settings.modeDevice')}</span>
              <span className="text-xs text-muted-foreground font-normal">{t('settings.modeDeviceHint')}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="bluetooth" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="mode-bluetooth">
              <span className="flex items-center gap-2 font-semibold"><Bluetooth className="h-4 w-4" /> {t('settings.modeBluetooth')}</span>
              <span className="text-xs text-muted-foreground font-normal">{t('settings.modeBluetoothHint')}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="agent" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="mode-agent">
              <span className="flex items-center gap-2 font-semibold"><Monitor className="h-4 w-4" /> {t('settings.modeAgent')}</span>
              <span className="text-xs text-muted-foreground font-normal">{t('settings.modeAgentHint')}</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* ===== MODALITÀ SERVER DIRECT PRINT (la stampante prende i lavori da sola) ===== */}
        {printer?.mode === 'sdp' && (
          <div className="rounded-xl border border-border p-4 mb-4" data-testid="printer-sdp-block">
            <div className="font-medium mb-1 flex items-center gap-2"><Cloud className="h-4 w-4" /> {t('settings.sdpTitle')}</div>
            <p className="text-sm text-muted-foreground mb-3">{t('settings.sdpDesc')}</p>

            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5 text-xs text-emerald-700 dark:text-emerald-400 mb-3 flex items-start gap-2">
              <Check className="h-4 w-4 shrink-0 mt-0.5" /><span>{t('settings.sdpAdvantage')}</span>
            </div>

            <Label>{t('settings.sdpUrl')}</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input readOnly value={pc?.agent_token ? `${BACKEND_URL}/api/print/sdp/${pc.agent_token}` : ''} className="font-mono text-xs" data-testid="sdp-url" />
              <Button type="button" variant="outline" size="icon" className="rounded-lg shrink-0" data-testid="sdp-copy-button"
                onClick={() => { const u = pc?.agent_token ? `${BACKEND_URL}/api/print/sdp/${pc.agent_token}` : ''; if (u) { navigator.clipboard?.writeText(u); toast.success(t('settings.copied')); } }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3 max-w-[220px]">
              <Label>{t('settings.paperWidth')}</Label>
              <Select value={String(printer?.paper_width || 80)} onValueChange={(v) => setPf('paper_width', Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="printer-paper-select-sdp"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="80">80 mm</SelectItem>
                  <SelectItem value="58">58 mm</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-3 flex items-center justify-between rounded-xl border border-border p-3" data-testid="open-drawer-block-sdp">
              <div className="pr-3">
                <div className="font-medium">{t('settings.openDrawer', { defaultValue: 'Apri cassetto automatico' })}</div>
                <div className="text-sm text-muted-foreground">{t('settings.openDrawerDesc', { defaultValue: 'Apre il cassetto portasoldi (12-24V) subito dopo lo scontrino di incasso.' })}</div>
              </div>
              <Switch checked={printer?.open_drawer !== false} onCheckedChange={(v) => setPf('open_drawer', v)} data-testid="open-drawer-toggle-sdp" />
            </div>

            <div className="mt-4">
              <Button type="button" onClick={savePrinter} disabled={savingPrinter} className="rounded-xl" data-testid="printer-save-button-sdp"><Save className="h-4 w-4 mr-2" /> {pc?.sdp_enabled ? t('settings.savePrinter') : t('settings.sdpEnable')}</Button>
            </div>

            <Guide testid="sdp-help" title={t('settings.sdpHelpTitle')}>
              <div>1. {t('settings.sdpHelp1')}</div>
              <div>2. {t('settings.sdpHelp2')}</div>
              <div>3. {t('settings.sdpHelp3')}</div>
              <div>4. {t('settings.sdpHelp4')}</div>
            </Guide>
          </div>
        )}

        {/* ===== MODALITÀ DISPOSITIVO (Epson ePOS-Print, dal browser) ===== */}
        {(printer?.mode || 'device') === 'device' && (
          <div className="rounded-xl border border-border p-4 mb-4" data-testid="printer-device-block">
            <div className="font-medium mb-1 flex items-center gap-2"><Wifi className="h-4 w-4" /> {t('settings.deviceDirect')}</div>
            <p className="text-sm text-muted-foreground mb-3">{t('settings.deviceDirectDesc')}</p>
            <datalist id="saved-printer-ips">
              {savedIps.map((ip) => <option key={ip} value={ip} />)}
            </datalist>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>{t('settings.printerHost')}</Label>
                <Input list="saved-printer-ips" value={printer?.host || ''} onChange={(e) => setPf('host', e.target.value)} placeholder="172.16.10.1" className="mt-1.5 font-mono" data-testid="printer-host-input" />
              </div>
              <div>
                <Label>{t('settings.protocol')}</Label>
                <Select value={printer?.protocol || 'https'} onValueChange={(v) => setPf('protocol', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="printer-protocol-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="https">HTTPS</SelectItem>
                    <SelectItem value="http">HTTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3 max-w-[220px]">
              <Label>{t('settings.paperWidth')}</Label>
              <Select value={String(printer?.paper_width || 80)} onValueChange={(v) => setPf('paper_width', Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="printer-paper-select-device"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="80">80 mm</SelectItem>
                  <SelectItem value="58">58 mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl border border-border p-3" data-testid="open-drawer-block-device">
              <div className="pr-3">
                <div className="font-medium">{t('settings.openDrawer', { defaultValue: 'Apri cassetto automatico' })}</div>
                <div className="text-sm text-muted-foreground">{t('settings.openDrawerDesc', { defaultValue: 'Apre il cassetto portasoldi (12-24V) subito dopo lo scontrino di incasso.' })}</div>
              </div>
              <Switch checked={printer?.open_drawer !== false} onCheckedChange={(v) => setPf('open_drawer', v)} data-testid="open-drawer-toggle-device" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button type="button" onClick={savePrinter} disabled={savingPrinter} className="rounded-xl" data-testid="printer-save-button"><Save className="h-4 w-4 mr-2" /> {t('settings.savePrinter')}</Button>
              <Button asChild variant="secondary" className="rounded-xl" data-testid="printer-cert-button">
                <a href={printer?.host ? printerBaseUrl({ ...printer, protocol: 'https' }) : '#'} target="_blank" rel="noopener noreferrer"><ShieldCheck className="h-4 w-4 mr-2" /> {t('settings.acceptCert')}</a>
              </Button>
              <Button type="button" variant="outline" onClick={testEposPrint} disabled={testingEpos} className="rounded-xl" data-testid="printer-epos-test-button"><Send className="h-4 w-4 mr-2" /> {t('settings.testPrint')}</Button>
            </div>
            {printer?.host && (
              <div className="mt-2 text-xs text-muted-foreground" data-testid="printer-cert-link">
                {t('settings.acceptCertManual') || 'Se non si apre, tocca qui:'}{' '}
                <a href={printerBaseUrl({ ...printer, protocol: 'https' })} target="_blank" rel="noopener noreferrer" className="font-mono text-primary underline break-all">
                  {printerBaseUrl({ ...printer, protocol: 'https' })}
                </a>
              </div>
            )}
            {printer?.protocol === 'http' && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400" data-testid="http-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('settings.httpWarning')}</span>
              </div>
            )}
            <Guide testid="device-help" title={t('settings.deviceHelpTitle')}>
              <div>1. {t('settings.deviceHelp1')}</div>
              <div>2. {t('settings.deviceHelp2')}</div>
              <div>3. {t('settings.deviceHelp3')}</div>
            </Guide>
          </div>
        )}

        {/* ===== MODALITÀ BLUETOOTH (Web Bluetooth, dal dispositivo) ===== */}
        {printer?.mode === 'bluetooth' && (
          <div className="rounded-xl border border-border p-4 mb-4" data-testid="printer-bt-block">
            <div className="font-medium mb-1 flex items-center gap-2"><Bluetooth className="h-4 w-4" /> {t('settings.btDirect')}</div>
            <p className="text-sm text-muted-foreground mb-3">{t('settings.btDirectDesc')}</p>

            {!isWebBluetoothSupported() && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400" data-testid="bt-unsupported">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('settings.btUnsupported')}</span>
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-border p-3 mb-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{t('settings.btStatus')}</div>
                <div className="text-sm text-muted-foreground truncate" data-testid="bt-status">
                  {btName ? <span className="text-pos-free font-medium">{btName}</span> : t('settings.btNotConnected')}
                </div>
              </div>
              <Button type="button" onClick={connectBt} disabled={btBusy || !isWebBluetoothSupported()} className="rounded-xl shrink-0" data-testid="bt-connect-button">
                <Bluetooth className="h-4 w-4 mr-2" /> {btName ? t('settings.btReconnect') : t('settings.btConnect')}
              </Button>
            </div>

            <div className="max-w-[220px]">
              <Label>{t('settings.paperWidth')}</Label>
              <Select value={String(printer?.paper_width || 80)} onValueChange={(v) => setPf('paper_width', Number(v))}>
                <SelectTrigger className="mt-1.5" data-testid="printer-paper-select-bt"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="80">80 mm</SelectItem>
                  <SelectItem value="58">58 mm</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button type="button" onClick={savePrinter} disabled={savingPrinter} className="rounded-xl" data-testid="printer-save-button-bt"><Save className="h-4 w-4 mr-2" /> {t('settings.savePrinter')}</Button>
              <Button type="button" variant="outline" onClick={testBtPrint} disabled={btBusy} className="rounded-xl" data-testid="printer-bt-test-button"><Send className="h-4 w-4 mr-2" /> {t('settings.testPrint')}</Button>
            </div>

            <Guide testid="bt-help" title={t('settings.btHelpTitle')}>
              <div>1. {t('settings.btHelp1')}</div>
              <div>2. {t('settings.btHelp2')}</div>
              <div>3. {t('settings.btHelp3')}</div>
            </Guide>
          </div>
        )}

        {/* ===== MODALITÀ AGENTE (programma su PC locale) ===== */}
        {printer?.mode === 'agent' && (
        <div data-testid="printer-agent-block">
        <div className="flex items-center justify-between rounded-xl border border-border p-3 mb-4">
          <div>
            <div className="font-medium">{t('settings.autoPrintAgent')}</div>
            <div className="text-sm text-muted-foreground">{t('settings.autoPrintDesc')}</div>
          </div>
          <Switch checked={!!pc?.agent_enabled} onCheckedChange={togglePrintAgent} data-testid="print-agent-toggle" />
        </div>

        {/* ----- Connessione stampante (Bluetooth / WLAN / USB) ----- */}
        <div className="rounded-xl border border-border p-4 mb-4" data-testid="printer-connection-block">
          <div className="font-medium mb-1">{t('settings.printerConnection')}</div>
          <p className="text-sm text-muted-foreground mb-3">{t('settings.printerConnectionDesc')}</p>

          <ToggleGroup
            type="single"
            value={printer?.connection || 'usb'}
            onValueChange={(v) => v && setPf('connection', v)}
            className="grid grid-cols-3 gap-2 mb-4"
            data-testid="printer-connection-toggle"
          >
            <ToggleGroupItem value="bluetooth" className="h-auto flex-col gap-1 rounded-xl border border-border py-3 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="conn-bluetooth">
              <Bluetooth className="h-5 w-5" />
              <span className="text-xs font-medium">{t('settings.connBluetooth')}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="network" className="h-auto flex-col gap-1 rounded-xl border border-border py-3 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="conn-network">
              <Wifi className="h-5 w-5" />
              <span className="text-xs font-medium">{t('settings.connNetwork')}</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="usb" className="h-auto flex-col gap-1 rounded-xl border border-border py-3 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid="conn-usb">
              <Usb className="h-5 w-5" />
              <span className="text-xs font-medium">{t('settings.connUsb')}</span>
            </ToggleGroupItem>
          </ToggleGroup>

          {/* WLAN / rete: IP + porta */}
          {printer?.connection === 'network' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="printer-network-fields">
              <div className="sm:col-span-2">
                <Label>{t('settings.printerHost')}</Label>
                <Input list="saved-printer-ips" value={printer?.host || ''} onChange={(e) => setPf('host', e.target.value)} placeholder="192.168.1.50" className="mt-1.5 font-mono" data-testid="printer-host-input-agent" />
              </div>
              <div>
                <Label>{t('settings.printerPort')}</Label>
                <Input type="number" value={printer?.port ?? 9100} onChange={(e) => setPf('port', Number(e.target.value))} placeholder="9100" className="mt-1.5 font-mono" data-testid="printer-port-input" />
              </div>
            </div>
          )}

          {/* USB: stampante installata nel sistema */}
          {printer?.connection === 'usb' && (
            <div className="space-y-2" data-testid="printer-usb-fields">
              <Label>{t('settings.printerSystemName')}</Label>
              {(pc?.discovered_printers || []).length > 0 && (
                <Select value={printer?.system_name || ''} onValueChange={(v) => setPf('system_name', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="printer-system-select"><SelectValue placeholder={t('settings.selectPrinter')} /></SelectTrigger>
                  <SelectContent>
                    {(pc.discovered_printers || []).map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <Input value={printer?.system_name || ''} onChange={(e) => setPf('system_name', e.target.value)} placeholder={t('settings.printerNamePlaceholder')} className="font-mono" data-testid="printer-system-input" />
            </div>
          )}

          {/* Bluetooth: porta seriale + baud */}
          {printer?.connection === 'bluetooth' && (
            <div className="space-y-3" data-testid="printer-bluetooth-fields">
              <div>
                <Label>{t('settings.printerSerialPort')}</Label>
                {(pc?.discovered_serial || []).length > 0 && (
                  <Select value={printer?.serial_port || ''} onValueChange={(v) => setPf('serial_port', v)}>
                    <SelectTrigger className="mt-1.5" data-testid="printer-serial-select"><SelectValue placeholder={t('settings.selectPort')} /></SelectTrigger>
                    <SelectContent>
                      {(pc.discovered_serial || []).map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
                    </SelectContent>
                  </Select>
                )}
                <Input value={printer?.serial_port || ''} onChange={(e) => setPf('serial_port', e.target.value)} placeholder="COM3 / /dev/rfcomm0" className="mt-1.5 font-mono" data-testid="printer-serial-input" />
              </div>
              <div className="max-w-[180px]">
                <Label>{t('settings.printerBaud')}</Label>
                <Input type="number" value={printer?.baud_rate ?? 9600} onChange={(e) => setPf('baud_rate', Number(e.target.value))} placeholder="9600" className="mt-1.5 font-mono" data-testid="printer-baud-input" />
              </div>
            </div>
          )}

          {/* Larghezza carta (comune) */}
          <div className="mt-3 max-w-[220px]">
            <Label>{t('settings.paperWidth')}</Label>
            <Select value={String(printer?.paper_width || 80)} onValueChange={(v) => setPf('paper_width', Number(v))}>
              <SelectTrigger className="mt-1.5" data-testid="printer-paper-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="80">80 mm</SelectItem>
                <SelectItem value="58">58 mm</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Apertura cassetto (12-24V) dopo lo scontrino di incasso */}
          <div className="mt-3 flex items-center justify-between rounded-xl border border-border p-3" data-testid="open-drawer-block-agent">
            <div className="pr-3">
              <div className="font-medium">{t('settings.openDrawer', { defaultValue: 'Apri cassetto automatico' })}</div>
              <div className="text-sm text-muted-foreground">{t('settings.openDrawerDesc', { defaultValue: 'Apre il cassetto portasoldi (12-24V) subito dopo lo scontrino di incasso.' })}</div>
            </div>
            <Switch checked={printer?.open_drawer !== false} onCheckedChange={(v) => setPf('open_drawer', v)} data-testid="open-drawer-toggle-agent" />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button type="button" onClick={savePrinter} disabled={savingPrinter} className="rounded-xl" data-testid="printer-save-button-agent">
              <Save className="h-4 w-4 mr-2" /> {t('settings.savePrinter')}
            </Button>
            <Button type="button" variant="outline" onClick={refreshDiscovered} className="rounded-xl" data-testid="printer-refresh-button">
              <Search className="h-4 w-4 mr-2" /> {t('settings.detectPrinters')}
            </Button>
            {pc?.discovered_at && (
              <span className="text-xs text-muted-foreground" data-testid="printer-discovered-info">
                {t('settings.lastDetected')} {new Date(pc.discovered_at).toLocaleTimeString(i18n.language)} · {(pc?.discovered_printers || []).length + (pc?.discovered_serial || []).length} {t('settings.devicesFound')}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label>{t('settings.cloudUrl')}</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input readOnly value={BACKEND_URL || ''} className="font-mono text-xs" data-testid="print-cloud-url" />
            </div>
          </div>
          <div>
            <Label>{t('settings.agentToken')}</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input readOnly type={showToken ? 'text' : 'password'} value={pc?.agent_token || ''} className="font-mono text-xs" data-testid="print-agent-token" />
              <Button type="button" variant="outline" className="rounded-lg shrink-0" onClick={() => setShowToken((v) => !v)}>{showToken ? t('common.hide') : t('common.show')}</Button>
              <Button type="button" variant="outline" size="icon" className="rounded-lg shrink-0" onClick={copyToken} title={t('settings.copyToken')}>{copied ? <Check className="h-4 w-4 text-pos-free" /> : <Copy className="h-4 w-4" />}</Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1.5">{t('settings.agentConfigHint1')}<span className="font-mono">print-agent</span>{t('settings.agentConfigHint2')}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" onClick={testPrint} className="rounded-xl" data-testid="print-test-button"><Send className="h-4 w-4 mr-2" /> {t('settings.testPrint')}</Button>
            <Button type="button" variant="outline" onClick={regenerateToken} className="rounded-xl" data-testid="print-regenerate-token"><RefreshCw className="h-4 w-4 mr-2" /> {t('settings.regenerateToken')}</Button>
            <span className="text-xs text-muted-foreground">{t('settings.supportedStations')} {(pc?.stations || ['cassa', 'cucina', 'bar']).join(' · ')}</span>
          </div>
        </div>
        </div>
        )}
      </Card>

      {/* ===== STAMPANTI PER POSTAZIONE (comande cucina / bar) ===== */}
      <Card className="p-5" data-testid="station-printers-card">
        <div className="font-semibold mb-1 flex items-center gap-2"><Printer className="h-4 w-4" /> {t('settings.stationPrinters')}</div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.stationPrintersDesc')}
        </p>

        <div className="space-y-4">
          {[
            { id: 'cucina', label: t('settings.stationKitchen'), Icon: ChefHat },
            { id: 'bar', label: t('settings.stationBar'), Icon: Wine },
          ].map(({ id, label, Icon }) => {
            const sp = stationPrinters[id] || {};
            const mode = sp.mode || 'wifi';
            return (
              <div key={id} className="rounded-xl border border-border p-4" data-testid={`station-block-${id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium flex items-center gap-2"><Icon className="h-4 w-4" /> {label}</div>
                  <Switch checked={!!sp.enabled} onCheckedChange={(v) => setSp(id, 'enabled', v)} data-testid={`station-enabled-${id}`} />
                </div>

                {sp.enabled && (
                  <div className="mt-3 space-y-3">
                    <ToggleGroup
                      type="single"
                      value={mode === 'off' ? 'wifi' : mode}
                      onValueChange={(v) => v && setSp(id, 'mode', v)}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                      data-testid={`station-mode-${id}`}
                    >
                      <ToggleGroupItem value="wifi" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid={`station-mode-wifi-${id}`}>
                        <span className="flex items-center gap-2 font-semibold"><Wifi className="h-4 w-4" /> {t('settings.wifiPrinter')}</span>
                        <span className="text-xs text-muted-foreground font-normal">{t('settings.wifiPrinterDesc')}</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem value="agent" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid={`station-mode-agent-${id}`}>
                        <span className="flex items-center gap-2 font-semibold"><Monitor className="h-4 w-4" /> {t('settings.stationModeAgent', { defaultValue: 'Agente PC (stampante dedicata)' })}</span>
                        <span className="text-xs text-muted-foreground font-normal">{t('settings.stationModeAgentDesc', { defaultValue: "L'Agente di stampa invia le comande a una stampante di rete dedicata." })}</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem value="sdp" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid={`station-mode-sdp-${id}`}>
                        <span className="flex items-center gap-2 font-semibold"><Cloud className="h-4 w-4" /> {t('settings.stationModeSdp', { defaultValue: 'Server Direct Print' })}</span>
                        <span className="text-xs text-muted-foreground font-normal">{t('settings.stationModeSdpDesc', { defaultValue: 'La stampante stampa da sola, senza dispositivi.' })}</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem value="local" className="h-auto flex-col items-start gap-1 rounded-xl border border-border p-3 text-left data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-primary" data-testid={`station-mode-local-${id}`}>
                        <span className="flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4" /> {t('settings.fromDevice')}</span>
                        <span className="text-xs text-muted-foreground font-normal">{t('settings.fromDeviceDesc')}</span>
                      </ToggleGroupItem>
                    </ToggleGroup>

                    {mode === 'agent' && (
                      <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-3" data-testid={`station-agent-block-${id}`}>
                        <div className="text-sm font-medium flex items-center gap-2"><Monitor className="h-4 w-4 text-primary" /> {t('settings.stationAgentTitle', { defaultValue: 'Stampante dedicata via Agente PC' })}</div>
                        <p className="text-xs text-muted-foreground">
                          {t('settings.stationAgentDesc', { defaultValue: "Le comande di questa postazione vengono stampate dall'Agente di stampa su una stampante di RETE dedicata (IP diverso dalla principale). Assicurati che l'Agente di stampa sia attivo." })}
                        </p>
                        {!pc?.agent_enabled && (
                          <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400" data-testid={`station-agent-warn-${id}`}>
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>{t('settings.stationAgentNeedAgent', { defaultValue: "L'Agente di stampa non è attivo. Attivalo nella sezione 'Stampante principale' → modalità Agente." })}</span>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-2">
                            <Label>{t('settings.printerHost')}</Label>
                            <Input list="saved-printer-ips" value={sp.host || ''} onChange={(e) => setSp(id, 'host', e.target.value)} placeholder="192.168.1.51" className="mt-1.5 font-mono" data-testid={`station-agent-host-${id}`} />
                          </div>
                          <div>
                            <Label>{t('settings.printerPort')}</Label>
                            <Input type="number" value={sp.port || 9100} onChange={(e) => setSp(id, 'port', Number(e.target.value))} placeholder="9100" className="mt-1.5 font-mono" data-testid={`station-agent-port-${id}`} />
                          </div>
                        </div>
                        <div className="max-w-[220px]">
                          <Label>{t('settings.paperWidth')}</Label>
                          <Select value={String(sp.paper_width || 80)} onValueChange={(v) => setSp(id, 'paper_width', Number(v))}>
                            <SelectTrigger className="mt-1.5" data-testid={`station-agent-paper-${id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="80">80 mm</SelectItem>
                              <SelectItem value="58">58 mm</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {mode === 'wifi' && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-2">
                            <Label>{t('settings.printerIp')}</Label>
                            <Input list="saved-printer-ips" value={sp.host || ''} onChange={(e) => setSp(id, 'host', e.target.value)} placeholder="10.49.133.64" className="mt-1.5 font-mono" data-testid={`station-host-${id}`} />
                          </div>
                          <div>
                            <Label>{t('settings.protocol')}</Label>
                            <Select value={sp.protocol || 'https'} onValueChange={(v) => setSp(id, 'protocol', v)}>
                              <SelectTrigger className="mt-1.5" data-testid={`station-protocol-${id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="https">HTTPS</SelectItem>
                                <SelectItem value="http">HTTP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="max-w-[220px]">
                          <Label>{t('settings.paperWidth')}</Label>
                          <Select value={String(sp.paper_width || 80)} onValueChange={(v) => setSp(id, 'paper_width', Number(v))}>
                            <SelectTrigger className="mt-1.5" data-testid={`station-paper-${id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="80">80 mm</SelectItem>
                              <SelectItem value="58">58 mm</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => testStationPrint(id)} disabled={testingStation === id} className="rounded-xl" data-testid={`station-test-${id}`}>
                          <Send className="h-4 w-4 mr-2" /> {t('settings.testPrint')}
                        </Button>
                      </>
                    )}
                    {mode === 'sdp' && (
                      <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2" data-testid={`station-sdp-block-${id}`}>
                        <div className="text-sm font-medium flex items-center gap-2"><Cloud className="h-4 w-4 text-primary" /> {t('settings.stationSdpUrlTitle', { defaultValue: 'URL Server Direct Print (postazione)' })}</div>
                        <p className="text-xs text-muted-foreground">
                          {t('settings.stationSdpUrlDesc', { defaultValue: 'Incolla questo indirizzo nella funzione "Server Direct Print" della stampante Epson di questa postazione. La stampante interrogherà il server e stamperà le comande da sola, senza dipendere da alcun dispositivo.' })}
                        </p>
                        <div className="flex items-center gap-2">
                          <Input readOnly value={pc?.agent_token ? `${BACKEND_URL}/api/print/sdp/${pc.agent_token}?station=${id}` : ''} className="font-mono text-xs" data-testid={`station-sdp-url-${id}`} />
                          <Button type="button" variant="outline" size="icon" className="rounded-lg shrink-0" data-testid={`station-sdp-copy-${id}`}
                            onClick={() => { const u = pc?.agent_token ? `${BACKEND_URL}/api/print/sdp/${pc.agent_token}?station=${id}` : ''; if (u) { navigator.clipboard?.writeText(u); toast.success(t('settings.copied')); } }}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <Guide testid={`station-sdp-help-${id}`} title={t('settings.stationSdpHelpTitle', { defaultValue: 'Come configurare la stampante Epson' })}>
                          <div>1. {t('settings.stationSdpHelp1', { defaultValue: 'Apri la pagina web della stampante (https://IP-stampante) e accedi come amministratore.' })}</div>
                          <div>2. {t('settings.stationSdpHelp2', { defaultValue: 'Vai su "Server Direct Print" e incolla l\u2019URL qui sopra.' })}</div>
                          <div>3. {t('settings.stationSdpHelp3', { defaultValue: 'Imposta un intervallo di polling breve (es. 3-5 secondi) e attiva la funzione.' })}</div>
                          <div>4. {t('settings.stationSdpHelp4', { defaultValue: 'Salva. Le comande di questa postazione usciranno automaticamente.' })}</div>
                        </Guide>
                        <div className="max-w-[220px]">
                          <Label>{t('settings.paperWidth')}</Label>
                          <Select value={String(sp.paper_width || 80)} onValueChange={(v) => setSp(id, 'paper_width', Number(v))}>
                            <SelectTrigger className="mt-1.5" data-testid={`station-sdp-paper-${id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="80">80 mm</SelectItem>
                              <SelectItem value="58">58 mm</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    {mode === 'local' && (
                      <p className="text-xs text-muted-foreground">
                        {t('settings.localModeHint', { station: label })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          <Button type="button" onClick={saveStations} disabled={savingStations} className="rounded-xl" data-testid="station-save-button">
            <Save className="h-4 w-4 mr-2" /> {t('settings.saveStationPrinters')}
          </Button>
        </div>
      </Card>

      {/* Stampante CAMERIERE (server-side, per attività) — sezione richiudibile */}
      <Card className="p-0 mb-4 overflow-hidden" data-testid="waiter-printer-card">
        <details className="group">
          <summary className="flex cursor-pointer select-none items-center gap-2 p-5 font-semibold marker:content-none [&::-webkit-details-marker]:hidden">
            <Printer className="h-4 w-4" /> {t('settings.waiterPrinterTitle', { defaultValue: 'Stampante cameriere' })}
            {waiterPrn.enabled && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary" data-testid="waiter-printer-active-badge">{t('settings.stationEnabledBadge', { defaultValue: 'Attiva' })}</span>}
            <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-5 pb-5">
        <p className="text-sm text-muted-foreground mb-4">{t('settings.waiterPrinterDescServer', { defaultValue: 'Stampante di rete (Wi-Fi/Ethernet) per i camerieri: configurala una volta qui dalla cassa e vale per tutti i profili cameriere su qualsiasi dispositivo. Resoconto e scontrino fiscale escono da questa stampante, senza uscire dalla principale.' })}</p>

        <div className="flex items-center justify-between gap-4 py-2">
          <div>
            <div className="font-medium">{t('settings.waiterPrinterEnable2', { defaultValue: 'Attiva stampante cameriere' })}</div>
            <div className="text-sm text-muted-foreground">{t('settings.waiterPrinterEnableHint', { defaultValue: 'I camerieri stamperanno automaticamente su questa stampante.' })}</div>
          </div>
          <Switch checked={!!waiterPrn.enabled} onCheckedChange={(v) => setWf('enabled', v)} data-testid="waiter-printer-enable" />
        </div>

        {waiterPrn.enabled && (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>{t('settings.printerHost', { defaultValue: 'Indirizzo IP stampante' })}</Label>
                <Input value={waiterPrn.host || ''} onChange={(e) => setWf('host', e.target.value)} placeholder="192.168.1.50" className="mt-1.5 font-mono" data-testid="waiter-printer-host" />
              </div>
              <div>
                <Label>{t('settings.paperWidth', { defaultValue: 'Larghezza carta' })}</Label>
                <Select value={String(waiterPrn.paper_width || 80)} onValueChange={(v) => setWf('paper_width', Number(v))}>
                  <SelectTrigger className="mt-1.5" data-testid="waiter-paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="80">80mm</SelectItem><SelectItem value="58">58mm</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="button" onClick={saveWaiter} disabled={savingWaiter} className="rounded-xl" data-testid="waiter-printer-save"><Save className="h-4 w-4 mr-2" /> {t('common.save')}</Button>
              <Button type="button" variant="outline" onClick={testWaiter} disabled={testingWaiter} className="rounded-xl" data-testid="waiter-printer-test"><Printer className="h-4 w-4 mr-2" /> {t('settings.testPrint', { defaultValue: 'Prova di stampa' })}</Button>
            </div>
          </div>
        )}
          </div>
        </details>
      </Card>
      </>)}

      {activeCat === 'license' && (
      <Card className="p-5">
        <div className="font-semibold mb-4 flex items-center gap-2"><KeyRound className="h-4 w-4" /> {t('settings.license')}</div>
        {activeLicense ? (
          <div className="text-sm">
            <div className="flex items-center gap-2"><span className="text-xs px-2 py-1 rounded-full bg-pos-free/10 text-pos-free font-medium">{t('settings.licenseActive')}</span> <span className="font-mono">{activeLicense.key}</span></div>
            <div className="text-muted-foreground mt-1">{t('settings.activatedOn', { date: new Date(activeLicense.activated_at).toLocaleString(i18n.language) })}</div>
          </div>
        ) : <div className="text-sm text-muted-foreground">{t('settings.noActiveLicense')}</div>}
      </Card>
      )}

      {activeCat === 'fiscal' && (
      <div className="mt-4 space-y-4">
        <FiscalSettings />
        <SumUpSettings />
      </div>
      )}

      {activeCat === 'invoices' && (
      <div className="mt-4">
        <InvoicesList />
      </div>
      )}

      {activeCat === 'security' && (
      <div className="mt-4">
        <SecuritySettings />
      </div>
      )}
    </div>
  );
}
